import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "@/components/site/motion";

const LINKS = [
  { label: "Work", to: "/work", id: "nav-link-work", shine: true },
  { label: "About", to: "/about", id: "nav-link-about" },
  { label: "Services", section: "services", id: "nav-link-services" },
  { label: "Blog", to: "/blog", id: "nav-link-blog" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      window.lenis?.scrollTo(`#${id}`, { offset: -60 });
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
        className={`fixed inset-x-3 top-3 z-50 rounded-full transition-[left,right,background-color,border-color,box-shadow] duration-500 md:inset-x-5 md:top-4 ${
          scrolled ? "glass-strong lg:inset-x-40" : "border border-transparent bg-transparent"
        }`}
        data-testid="main-navigation"
      >
        <div
          className={`relative flex items-center justify-between transition-[padding] duration-500 ${
            scrolled ? "py-2.5 pl-5 pr-3" : "py-4 pl-7 pr-4"
          }`}
        >
          <Link
            to="/"
            data-testid="nav-logo"
            onClick={(e) => {
              if (location.pathname === "/") {
                e.preventDefault();
                window.lenis?.scrollTo(0);
              }
            }}
            className={`font-display font-semibold tracking-tight text-white transition-[font-size] duration-500 ${
              scrolled ? "text-xl" : "text-2xl"
            }`}
          >
            JARE
          </Link>

          <nav
            className={`absolute left-1/2 hidden -translate-x-1/2 items-center transition-[gap] duration-500 lg:flex ${
              scrolled ? "gap-6" : "gap-9"
            }`}
          >
            {LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={l.id}
                  className={`text-sm font-medium transition-colors duration-300 hover:text-white ${
                    l.shine ? "nav-shine" : location.pathname.startsWith(l.to) ? "text-white" : "text-neutral-400"
                  }`}
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.label}
                  onClick={() => goSection(l.section)}
                  data-testid={l.id}
                  className="text-sm font-medium text-neutral-400 transition-colors duration-300 hover:text-white"
                >
                  {l.label}
                </button>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-work-together"
              className={`group hidden items-center gap-1.5 rounded-full border font-mono text-[11px] uppercase tracking-[0.18em] text-white transition-[padding,border-color,background-color] duration-500 hover:border-white/60 hover:bg-white/[0.05] md:inline-flex ${
                scrolled ? "border-white/20 px-4 py-2" : "border-white/25 px-5 py-2.5"
              }`}
            >
              Let&apos;s Work Together
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              className="glass flex h-10 w-10 items-center justify-center rounded-full text-white lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col items-start justify-center bg-[#050505]/95 px-8 backdrop-blur-2xl lg:hidden"
            data-testid="mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.label}
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.08 * i + 0.1, duration: 0.6, ease: EASE }}
                className="overflow-hidden py-2"
              >
                {l.to ? (
                  <Link
                    to={l.to}
                    data-testid={`mobile-${l.id}`}
                    className={`font-display text-5xl font-bold tracking-tight text-white ${l.shine ? "nav-shine" : ""}`}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <button
                    onClick={() => goSection(l.section)}
                    data-testid={`mobile-${l.id}`}
                    className="font-display text-5xl font-bold tracking-tight text-white"
                  >
                    {l.label}
                  </button>
                )}
              </motion.div>
            ))}
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.08 * LINKS.length + 0.1, duration: 0.6, ease: EASE }}
              className="overflow-hidden py-2"
            >
              <Link
                to="/contact"
                data-testid="mobile-nav-link-contact"
                className="font-display text-5xl font-bold tracking-tight text-white"
              >
                Contact
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
